/**
 * VoiceToNotes — small mic button + inline recording that transcribes speech
 * (via Whisper) directly into a notes textarea. Certifier taps mic → speaks a
 * quick observation ("gap at north corner is 130mm, needs mesh") → releases →
 * the transcript is APPENDED to the existing notes value.
 *
 * Reused across every checklist item's Notes field.
 */
import { useState, useRef, useEffect } from "react";
import { Mic, Loader2, Square } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const VoiceToNotes = ({ inspectionId, currentNotes, onAppend, testid }) => {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => () => {
    if (mediaRef.current?.state === "recording") mediaRef.current.stop();
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const start = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error("Voice recording is not supported on this device");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
        : "audio/mp4";
      const mr = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        setTranscribing(true);
        try {
          const blob = new Blob(chunksRef.current, { type: mimeType });
          const form = new FormData();
          form.append("audio", blob, `voice_note_${Date.now()}.webm`);
          const r = await api.post(`/inspections/${inspectionId}/voice-transcribe`, form, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000,
          });
          const text = (r.data?.transcript || "").trim();
          if (!text) {
            toast.warning("Couldn't transcribe — try recording again");
          } else {
            const separator = currentNotes ? "\n" : "";
            onAppend(currentNotes + separator + text);
            toast.success("Transcribed to notes");
          }
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Transcription failed");
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (err) {
      const msg = err?.name === "NotAllowedError" ? "Microphone permission denied — enable it in your browser settings"
        : err?.name === "NotFoundError" ? "No microphone detected — check your device settings"
        : err?.message || "Could not start recording";
      toast.error(msg);
    }
  };

  const stop = () => {
    if (mediaRef.current?.state === "recording") mediaRef.current.stop();
  };

  const fmt = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,"0")}`;

  if (transcribing) {
    return (
      <button
        type="button"
        disabled
        data-testid={testid}
        className="h-7 px-2 inline-flex items-center gap-1 border-2 border-purple-300 bg-purple-50 text-purple-700 font-bold text-[10px] uppercase tracking-wider whitespace-nowrap"
      >
        <Loader2 size={11} className="animate-spin"/> Transcribing…
      </button>
    );
  }
  if (recording) {
    return (
      <button
        type="button"
        onClick={stop}
        data-testid={testid}
        className="h-7 px-2 inline-flex items-center gap-1 border-2 border-red-600 bg-red-600 text-white font-bold text-[10px] uppercase tracking-wider whitespace-nowrap animate-pulse"
      >
        <Square size={10} strokeWidth={3}/> Stop · {fmt(seconds)}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={start}
      data-testid={testid}
      title="Dictate a note with your voice (Whisper)"
      className="h-7 px-2 inline-flex items-center gap-1 border-2 border-purple-300 bg-white text-purple-700 font-bold text-[10px] uppercase tracking-wider hover:border-purple-500 hover:bg-purple-50 whitespace-nowrap"
    >
      <Mic size={11} strokeWidth={2.5}/> Dictate
    </button>
  );
};

export default VoiceToNotes;
